import { sep } from 'path';
import type { Package, AddonPackage } from '@embroider/shared-internals';

export interface RouteFiles {
  route?: string;
  template?: string;
  controller?: string;
  children: Map<string, RouteFiles>;
}

export class AppFiles {
  readonly components: ReadonlyArray<string>;
  readonly helpers: ReadonlyArray<string>;
  readonly modifiers: ReadonlyArray<string>;
  private perRoute: RouteFiles;
  readonly otherAppFiles: ReadonlyArray<string>;
  readonly isFastbootOnly: Map<string, boolean>;
  readonly fastbootFiles: { [appName: string]: { localFilename: string; shadowedFilename: string | undefined } };

  constructor(
    readonly engine: Engine,
    appFiles: Set<string>,
    fastbootFiles: Set<string>,
    resolvableExtensions: RegExp,
    staticAppPathsPattern: RegExp | undefined,
    podModulePrefix?: string
  ) {
    let components: string[] = [];
    let helpers: string[] = [];
    let modifiers: string[] = [];
    let otherAppFiles: string[] = [];
    this.perRoute = { children: new Map() };

    let combinedFiles = new Map<string, string>();
    let combinedNonFastbootFiles = new Map<string, string>();

    function add(map: Map<string, string>, filename: string): void {
      if (!resolvableExtensions.test(filename)) {
        return;
      }

      if (/\.d\.ts$/.test(filename)) {
        // .d.ts files are technically "*.ts" files but aren't really and we
        // don't want to include them when we crawl through the app.
        return;
      }

      filename = filename.split(sep).join('/');

      let extensionless = filename.replace(resolvableExtensions, '');
      let prior = map.get(extensionless);

      if (!prior) {
        // no prior, so we win
        map.set(extensionless, filename);
      } else if (prior.startsWith('components/') && prior.endsWith('.hbs') && !prior.endsWith('/template.hbs')) {
        // The prior entry in combinedFiles is a colocated hbs component. The
        // name "template.hbs" is special in the template colocation rules due
        // to earlier pods patterns.
        //
        // colocated hbs never wins over the corresponding other resolvable js
        // or ts.
        map.set(extensionless, filename);
      }
    }

    for (let f of appFiles) {
      add(combinedFiles, f);
      add(combinedNonFastbootFiles, f);
    }
    for (let f of fastbootFiles) {
      add(combinedFiles, f);
    }

    for (let addon of engine.addons.keys()) {
      let appJS = addon.meta['app-js'];
      if (appJS) {
        for (let filename of Object.keys(appJS)) {
          filename = filename.replace(/^\.\//, '');
          add(combinedFiles, filename);
          add(combinedNonFastbootFiles, filename);
        }
      }

      let fastbootJS = addon.meta['fastboot-js'];
      if (fastbootJS) {
        for (let filename of Object.keys(fastbootJS)) {
          filename = filename.replace(/^\.\//, '');
          add(combinedFiles, filename);
        }
      }
    }

    let isFastbootOnly = new Map<string, boolean>();
    for (let [extensionless, relativePath] of combinedFiles) {
      isFastbootOnly.set(relativePath, !combinedNonFastbootFiles.has(extensionless));

      if (relativePath.startsWith('tests/')) {
        // skip tests because they are dealt with separately
        continue;
      }

      if (relativePath.startsWith('components/')) {
        // hbs files are not resolvable when they're used via co-location with
        // an associated js file because it's the js that is resolvable.
        // An hbs file is used via colocation when it's inside the components
        // directory, and also not named "template.hbs" (because that is an
        // older pattern used with pods-like layouts).
        let isHbs = relativePath.endsWith('.hbs');
        if (!isHbs || relativePath.endsWith('/template.hbs')) {
          components.push(relativePath);
        } else if (isHbs) {
          // template-only components will be compiled as js files during the
          // build process, so we push a virtual path to js in the list of files
          // because the resolver will be able to recognize a template-only and
          // give a correct answer in the end.
          let jsPath = relativePath.replace(/\.hbs$/, '.js');
          components.push(jsPath);
        }
        continue;
      }

      if (relativePath.startsWith('templates/components/')) {
        components.push(relativePath);
        continue;
      }

      if (relativePath.startsWith('helpers/')) {
        helpers.push(relativePath);
        continue;
      }

      if (relativePath.startsWith('modifiers/')) {
        modifiers.push(relativePath);
        continue;
      }

      if (
        (podModulePrefix !== undefined && this.handlePodsRouteFile(relativePath, podModulePrefix)) ||
        this.handleClassicRouteFile(relativePath)
      ) {
        continue;
      }

      if (staticAppPathsPattern) {
        if (!staticAppPathsPattern.test(relativePath)) {
          otherAppFiles.push(relativePath);
        }
      } else {
        otherAppFiles.push(relativePath);
      }
    }
    this.components = components;
    this.helpers = helpers;
    this.modifiers = modifiers;
    this.otherAppFiles = otherAppFiles;
    this.isFastbootOnly = isFastbootOnly;

    // this deliberately only describes the app's fastboot files. Not the full
    // merge from all the addons. This is because they need different handling
    // in the module resolver -- addon fastboot files can always be a
    // fallbackResolve, because if the app happens to define the same name
    // (whether fastboot-specific or just browser) that wins over the addon.
    // Whereas if the app itself defines a fastbot-specific version of a file,
    // that must take precedence over the *normal* resolution, and must be
    // implemented in beforeResolve.
    this.fastbootFiles = Object.fromEntries(
      [...fastbootFiles].map(name => [
        `./${name}`,
        {
          localFilename: `./_fastboot_/${name}`,
          shadowedFilename: appFiles.has(name) ? `./${name}` : undefined,
        },
      ])
    );
  }

  private handleClassicRouteFile(relativePath: string): boolean {
    let [prefix, ...rest] = relativePath.replace(/\.\w{1,3}$/, '').split('/');
    if (!['controllers', 'templates', 'routes'].includes(prefix)) {
      return false;
    }
    let type = prefix.slice(0, -1) as 'controller' | 'template' | 'route';
    let cursor = this.perRoute;
    for (let part of rest) {
      let child = cursor.children.get(part);
      if (child) {
        cursor = child;
      } else {
        let newEntry = { children: new Map() };
        cursor.children.set(part, newEntry);
        cursor = newEntry;
      }
    }
    cursor[type] = relativePath;
    return true;
  }

  private handlePodsRouteFile(relativePath: string, podModulePrefix: string): boolean {
    let parts = relativePath.replace(/\.\w{1,3}$/, '').split('/');
    let type = parts.pop();
    if (!type || !['controller', 'template', 'route'].includes(type)) {
      return false;
    }
    let podParts = podModulePrefix.split('/');
    // The first part of podModulePrefix is the app's package name
    podParts.shift();

    for (let podPart of podParts) {
      if (parts.shift() !== podPart) {
        return false;
      }
    }

    let cursor = this.perRoute;
    for (let part of parts) {
      let child = cursor.children.get(part);
      if (child) {
        cursor = child;
      } else {
        let newEntry = { children: new Map() };
        cursor.children.set(part, newEntry);
        cursor = newEntry;
      }
    }
    cursor[type as 'controller' | 'template' | 'route'] = relativePath;
    return true;
  }

  get routeFiles(): Readonly<RouteFiles> {
    return this.perRoute;
  }
}

export interface Engine {
  // the engine's own package
  package: Package;
  // the set of active addons in the engine. For each one we keep track of a file that can resolve the addon, because we'll need that later.
  addons: Map<AddonPackage, string>;
  // is this the top-level engine?
  isApp: boolean;
  // runtime name for the engine's own module namespace
  modulePrefix: string;
  // TODO: remove this after we remove the stage2 entrypoint
  appRelativePath: string;
}
